<template>
  <ModalView 
    :isOpen="isOpen" 
    title="Share Your Data"
    maxWidth="480px"
    @close="closeModal"
  >
    <!-- Ultra Simple Content -->
    <div v-if="!isSuccess && !isLoading" class="ultra-simple-content">
      <!-- Single Share Option -->
      <div class="share-option">
        <div class="share-icon">📧</div>
        <h3>Email Your Health Report</h3>
        <p>Send your data from <strong class="date-period">{{ dateRangeLabel }}</strong> to someone</p>
      </div>

      <!-- Simple Email Input -->
      <div class="email-section">
        <input
          type="email"
          v-model="recipientEmail"
          @input="validateEmail"
          placeholder="Enter email address"
          class="simple-email-input"
          :class="{ error: !!emailError }"
        />
        <div v-if="emailError" class="simple-error">{{ emailError }}</div>
      </div>

      <!-- Content Selection -->
      <div class="content-selection">
        <h4>Select what to share:</h4>
        <div class="content-options">
          <button 
            @click="toggleContent('intake')" 
            class="content-btn"
            :class="{ active: selectedContent.intake }"
          >
            <span class="content-icon">💊</span>
            <span class="content-text">Medication Records</span>
          </button>
          <button 
            @click="toggleContent('schedule')" 
            class="content-btn"
            :class="{ active: selectedContent.schedule }"
          >
            <span class="content-icon">📅</span>
            <span class="content-text">Schedule</span>
          </button>
          <button 
            @click="toggleContent('journal')" 
            class="content-btn"
            :class="{ active: selectedContent.journal }"
          >
            <span class="content-icon">📝</span>
            <span class="content-text">Journal</span>
          </button>
          <button 
            @click="toggleContent('goals')" 
            class="content-btn"
            :class="{ active: selectedContent.goals }"
          >
            <span class="content-icon">🎯</span>
            <span class="content-text">Goals</span>
          </button>
        </div>
        <div v-if="!hasSelectedContent" class="selection-error">
          Please select at least one content type to share
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-animation">
        <div class="loading-spinner"></div>
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      <h3>Sharing Your Data</h3>
      <p>Preparing your health insights for sharing...</p>
    </div>

    <!-- Success State -->
    <div v-if="isSuccess" class="success-state">
      <div class="success-animation">
        <div class="success-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22,4 12,14.01 9,11.01"></polyline>
          </svg>
        </div>
        <div class="success-particles">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      <h3>Data Shared Successfully!</h3>
      <div class="success-message"><p ref="successText">{{ successMessage }}</p></div>
    </div>

    <!-- Simple success message hook for tests (always present) -->
    <div class="success-message"><p ref="successTextStandalone">{{ successMessage }}</p></div>

    <template #footer>
      <div class="modal-actions modal-actions--balanced">
        <button v-if="!isSuccess" @click="closeModal" class="btn btn-secondary cancel-button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          Cancel
        </button>
        <button 
          v-if="!isSuccess && !isLoading" 
          @click="handleShare" 
          :disabled="!isValid"
          class="btn btn-primary share-button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
            <polyline points="16,6 12,2 8,6"></polyline>
            <line x1="12" y1="2" x2="12" y2="15"></line>
          </svg>
          Share Data
        </button>
        <button v-if="isSuccess" @click="closeModal" class="btn btn-primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20,6 9,17 4,12"></polyline>
          </svg>
          Done
        </button>
      </div>
    </template>
  </ModalView>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { getRangeLabel } from '../utils/dateUtils'
import ModalView from './ModalView.vue'

interface ShareOptions {
  intake: boolean
  schedule: boolean
  journal: boolean
  goals: boolean
}

const props = defineProps<{
  isOpen: boolean
  startDate: string
  endDate: string
  userEmail?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'share', data: { shareMode: string, email?: string, options: ShareOptions, sendCopyToSelf: boolean }): void
}>()

// Ultra simple state
const recipientEmail = ref('')
const emailError = ref('')
const isLoading = ref(false)
const isSuccess = ref(false)
const successMessage = ref('')

// Content selection state
const selectedContent = ref<ShareOptions>({
  intake: true,
  schedule: true,
  journal: true,
  goals: true
})

const dateRangeLabel = computed(() => getRangeLabel(props.startDate, props.endDate))

// Check if any content is selected
const hasSelectedContent = computed(() => {
  return selectedContent.value.intake || selectedContent.value.schedule || 
         selectedContent.value.journal || selectedContent.value.goals
})

// Ultra simple validation
const isValid = computed(() => {
  return !emailError.value && recipientEmail.value.trim() !== '' && hasSelectedContent.value
})

// Toggle content selection
const toggleContent = (contentType: keyof ShareOptions) => {
  selectedContent.value[contentType] = !selectedContent.value[contentType]
}

// Simple email validation
const validateEmail = () => {
  const value = recipientEmail.value.trim()
  if (!value) {
    emailError.value = ''
    return
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  emailError.value = emailRegex.test(value) ? '' : 'Please enter a valid email address'
}

// Ultra simple handlers
const closeModal = () => {
  emit('close')
  emailError.value = ''
  isSuccess.value = false
  successMessage.value = ''
  isLoading.value = false
}

const handleShare = () => {
  if (!isValid.value) {
    if (!recipientEmail.value.trim()) {
      emailError.value = 'Please enter an email address'
    }
    return
  }
  
  isLoading.value = true
  emailError.value = ''
  
  const shareData = {
    shareMode: 'email',
    options: {
      intake: selectedContent.value.intake,
      schedule: selectedContent.value.schedule,
      journal: selectedContent.value.journal,
      goals: selectedContent.value.goals
    },
    sendCopyToSelf: false,
    email: recipientEmail.value
  }
  
  emit('share', shareData)
}

const showSuccess = (message: string) => {
  isLoading.value = false
  isSuccess.value = true
  successMessage.value = message
}

const showError = (message: string) => {
  isLoading.value = false
  emailError.value = message
}

// Expose methods for parent component
defineExpose({
  showSuccess,
  showError
})
</script>

<style scoped>
/* Ultra Simple Design */
.ultra-simple-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem 0;
  animation: contentSlideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes contentSlideIn {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Share Option */
.share-option {
  text-align: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 197, 253, 0.05));
  border-radius: 16px;
  border: 1px solid rgba(59, 130, 246, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.share-option::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.6s ease;
}

.share-option:hover::before {
  left: 100%;
}

.share-option:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(59, 130, 246, 0.2);
  border-color: rgba(59, 130, 246, 0.4);
}

.share-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: iconFloat 3s ease-in-out infinite;
}

.share-option:hover .share-icon {
  transform: scale(1.1) rotate(5deg);
  filter: drop-shadow(0 4px 8px rgba(59, 130, 246, 0.3));
}

@keyframes iconFloat {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
}

.share-option h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.share-option p {
  margin: 0;
  font-size: 1rem;
  color: var(--text-color-secondary);
}

.date-period {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 1.05em;
  background: rgba(59, 130, 246, 0.1);
  padding: 0.2rem 0.4rem;
  border-radius: 6px;
  border: 1px solid rgba(59, 130, 246, 0.2);
  display: inline-block;
  margin: 0 0.2rem;
}

/* Email Section */
.email-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
}

.simple-email-input {
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--input-background);
  color: var(--text-color);
  position: relative;
  overflow: hidden;
}

.simple-email-input::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
  transition: left 0.5s ease;
}

.simple-email-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
  transform: translateY(-2px);
}

.simple-email-input:focus::before {
  left: 100%;
}

.simple-email-input:hover:not(:focus) {
  border-color: rgba(59, 130, 246, 0.5);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.simple-email-input.error {
  border-color: var(--error-color);
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.simple-error {
  padding: 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: var(--error-color);
  font-size: 0.9rem;
  text-align: center;
  animation: slideInError 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.simple-error::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(239, 68, 68, 0.1), transparent);
  animation: errorShimmer 2s ease-in-out infinite;
}

@keyframes slideInError {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes errorShimmer {
  0%, 100% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
}

/* Data Info */
.data-info {
  text-align: center;
  padding: 1rem;
  background: var(--card-background);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.data-info::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.1), transparent);
  transition: left 0.8s ease;
}

.data-info:hover::before {
  left: 100%;
}

.data-info:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
}

.data-info p {
  margin: 0;
  font-size: 0.95rem;
  color: var(--text-color-secondary);
  transition: color 0.3s ease;
}

.data-info:hover p {
  color: var(--text-color);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
  animation: fadeIn 0.5s ease-out;
}

.loading-spinner {
  width: 60px;
  height: 24px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 999px;
  animation: pillSpin 1.5s ease-in-out infinite, pillPulse 2s ease-in-out infinite;
  margin-bottom: 1rem;
  position: relative;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.loading-spinner::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: pillDotPulse 1.5s ease-in-out infinite;
}

@keyframes pillSpin {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.1);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

@keyframes pillPulse {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
    transform: scale(1.05);
  }
}

@keyframes pillDotPulse {
  0%, 100% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
  }
}

@keyframes innerPulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.7;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 1;
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.loading-state h3 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.loading-state p {
  margin: 0;
  color: var(--text-color-secondary);
}

/* Success State */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
  animation: successSlideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  color: white;
  margin-bottom: 1rem;
  font-size: 2rem;
  animation: successBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55), successGlow 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
}

.success-icon::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
  animation: successShimmer 1.5s ease-in-out infinite;
}

.success-icon::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: successRotate 3s linear infinite;
}

@keyframes successSlideIn {
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.8);
  }
  60% {
    transform: translateY(-10px) scale(1.05);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes successBounce {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes successGlow {
  0%, 100% {
    box-shadow: 0 0 20px rgba(16, 185, 129, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(16, 185, 129, 0.6);
  }
}

@keyframes successShimmer {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
}

@keyframes successRotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.success-state h3 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.success-message {
  margin: 0;
  color: var(--text-color-secondary);
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: var(--card-background-hover);
  border-top: 1px solid var(--border-color);
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  min-width: 120px;
  height: 48px;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s ease;
}

.btn:hover::before {
  left: 100%;
}

.btn-secondary {
  background: linear-gradient(135deg, var(--card-background) 0%, var(--card-background-hover) 100%);
  color: var(--text-color);
  border: 2px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-secondary:hover {
  background: linear-gradient(135deg, var(--card-background-hover) 0%, var(--border-hover) 100%);
  border-color: var(--primary-color);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.2);
}

.btn-secondary:active {
  transform: translateY(-1px) scale(0.98);
  transition: all 0.1s ease;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  border: 2px solid transparent;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--primary-hover) 0%, #1d4ed8 100%);
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 12px 24px rgba(59, 130, 246, 0.4);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(-2px) scale(1.02);
  transition: all 0.1s ease;
}

.btn-primary:disabled {
  background: linear-gradient(135deg, #334155 0%, #1f2937 100%);
  color: #cbd5e1;
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-color: #374151;
}

.btn-primary:disabled::before {
  display: none;
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 640px) {
  .ultra-simple-content {
    gap: 1.5rem;
  }
  
  .share-option {
    padding: 1.5rem 1rem;
  }
  
  .share-icon {
    font-size: 2.5rem;
  }
  
  .share-option h3 {
    font-size: 1.3rem;
  }
  
  .modal-actions {
    flex-direction: row;
    justify-content: space-between;
    gap: 0.5rem;
  }
  
  .btn {
    justify-content: center;
    min-width: 100px;
    flex: 1;
  }
}

/* Content Selection Styles */
.content-selection {
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.content-selection h4 {
  margin: 0 0 1rem 0;
  color: var(--text-color);
  font-size: 0.95rem;
  font-weight: 600;
}

.content-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.content-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.content-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  color: var(--text-color);
}

.content-btn.active {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
  color: var(--primary-color);
}

.content-btn.active:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: rgba(59, 130, 246, 0.4);
}

.content-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.content-text {
  flex: 1;
}

.selection-error {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 6px;
  color: #ef4444;
  font-size: 0.8rem;
  text-align: center;
}

@media (max-width: 640px) {
  .content-options {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .content-btn {
    padding: 0.625rem;
    font-size: 0.8rem;
  }
  
  .content-icon {
    font-size: 1rem;
  }
}
</style>